import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Network, Plus, Trash, RefreshCw, LinkIcon, Globe, Cloud } from 'lucide-react';
import { NetworkType, NetworkProvider, NetworkInfrastructure } from '@/components/dashboard/network/NetworkTypes';

interface SimpleNetworkItem {
  id: string;
  name: string;
  type: NetworkType;
  provider: NetworkProvider;
  status: 'active' | 'inactive' | 'maintenance';
  nodes: number;
}

// Sample network data
const sampleNetworks: SimpleNetworkItem[] = [
  { id: 'net-1', name: 'Cloud Mesh East', type: 'mesh', provider: 'aws', status: 'active', nodes: 12 },
  { id: 'net-2', name: 'Edge 5G Network', type: '5g', provider: 'att', status: 'active', nodes: 8 },
  { id: 'net-3', name: 'Backup Fiber Link', type: 'fiber', provider: 'verizon', status: 'inactive', nodes: 4 },
];

const NetworkManager = () => {
  const [networks, setNetworks] = useState<SimpleNetworkItem[]>(sampleNetworks);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newNetwork, setNewNetwork] = useState({
    name: '',
    type: 'mesh' as NetworkType,
    provider: 'aws' as NetworkProvider,
    infrastructure: 'vm' as NetworkInfrastructure
  });

  const handleDeleteNetwork = (id: string) => {
    setNetworks(networks.filter(network => network.id !== id));
    toast.success("Network deleted", {
      description: "The network has been removed from your infrastructure."
    });
  };

  const handleToggleStatus = (id: string) => {
    setNetworks(networks.map(network => 
      network.id === id 
        ? { ...network, status: network.status === 'active' ? 'inactive' : 'active' } 
        : network
    ));
    
    const network = networks.find(n => n.id === id);
    toast.success(
      `Network ${network?.status === 'active' ? 'deactivated' : 'activated'}`,
      {
        description: `${network?.name} has been ${network?.status === 'active' ? 'deactivated' : 'activated'}.`
      }
    );
  };

  const getNetworkIcon = (type: NetworkType) => {
    switch(type) {
      case 'mesh': return <Globe className="h-5 w-5" />;
      case '5g': return <Network className="h-5 w-5" />;
      case 'fiber': return <LinkIcon className="h-5 w-5" />;
      case 'cloud': return <Cloud className="h-5 w-5" />;
      default: return <Network className="h-5 w-5" />;
    }
  };

  const handleCreateNetwork = () => {
    const newId = `net-${Date.now().toString().slice(-4)}`;
    const createdNetwork = {
      id: newId,
      name: newNetwork.name,
      type: newNetwork.type,
      provider: newNetwork.provider,
      status: 'active' as const,
      nodes: 1
    };
    
    setNetworks([...networks, createdNetwork]);
    setShowNewForm(false);
    setNewNetwork({
      name: '',
      type: 'mesh',
      provider: 'aws',
      infrastructure: 'vm'
    });
    
    toast.success("Network created", {
      description: `${newNetwork.name} has been successfully created.`
    });
  };

  const handleRunAIOptimization = (id: string) => {
    const network = networks.find(n => n.id === id);
    toast.success("AI Optimization Started", {
      description: `Optimizing ${network?.name}. This will take a few minutes.`
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center">
          <Network className="mr-2 h-5 w-5" />
          Network Management
        </h2>
        <Button onClick={() => setShowNewForm(!showNewForm)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Network
        </Button>
      </div>
      
      {showNewForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Network</CardTitle>
            <CardDescription>Define your network configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Network Name</Label>
                <Input 
                  id="name" 
                  value={newNetwork.name} 
                  onChange={(e) => setNewNetwork({...newNetwork, name: e.target.value})}
                  placeholder="e.g. Production Mesh Network"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="type">Network Type</Label>
                  <Select 
                    value={newNetwork.type} 
                    onValueChange={(value) => setNewNetwork({...newNetwork, type: value as NetworkType})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mesh">Mesh Network</SelectItem>
                      <SelectItem value="5g">5G Network</SelectItem>
                      <SelectItem value="fiber">Fiber Network</SelectItem>
                      <SelectItem value="cloud">Cloud Network</SelectItem>
                      <SelectItem value="hybrid">Hybrid Network</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="provider">Cloud Provider</Label>
                  <Select 
                    value={newNetwork.provider} 
                    onValueChange={(value) => setNewNetwork({...newNetwork, provider: value as NetworkProvider})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aws">AWS</SelectItem>
                      <SelectItem value="google">Google Cloud</SelectItem>
                      <SelectItem value="azure">Azure</SelectItem>
                      <SelectItem value="att">AT&T</SelectItem>
                      <SelectItem value="verizon">Verizon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="infrastructure">Infrastructure Type</Label>
                <Select 
                  value={newNetwork.infrastructure} 
                  onValueChange={(value) => setNewNetwork({...newNetwork, infrastructure: value as NetworkInfrastructure})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select infrastructure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vm">Virtual Machines</SelectItem>
                    <SelectItem value="container">Containers</SelectItem>
                    <SelectItem value="dedicated">Dedicated Hardware</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setShowNewForm(false)}>Cancel</Button>
            <Button onClick={handleCreateNetwork} disabled={!newNetwork.name}>Create Network</Button>
          </CardFooter>
        </Card>
      )}
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {networks.map((network) => (
          <Card key={network.id} className={`${network.status === 'inactive' ? 'opacity-70' : ''}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  {getNetworkIcon(network.type)}
                  <span className="ml-2">{network.name}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Switch 
                    checked={network.status === 'active'} 
                    onCheckedChange={() => handleToggleStatus(network.id)} 
                  />
                </div>
              </CardTitle>
              <CardDescription className="flex justify-between items-center">
                <span>{network.type.charAt(0).toUpperCase() + network.type.slice(1)} Network</span>
                <span className="text-xs bg-muted px-2 py-1 rounded-full">{network.provider.toUpperCase()}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`font-medium ${
                    network.status === 'active' ? 'text-green-500' :
                    network.status === 'maintenance' ? 'text-yellow-500' : 'text-gray-500'
                  }`}>
                    {network.status.charAt(0).toUpperCase() + network.status.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Connected Nodes:</span>
                  <span className="font-medium">{network.nodes}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 h-8"
                onClick={() => handleRunAIOptimization(network.id)}
                disabled={network.status !== 'active'}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                AI Optimize
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 aspect-square p-0"
                onClick={() => handleDeleteNetwork(network.id)}
              >
                <Trash className="h-3 w-3" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default NetworkManager;
